import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Users, Target, DollarSign, Shield, Crown,
  Ban, Calendar, MoreVertical, Activity, ArrowUpRight, ArrowDownRight,
  Search, RefreshCw, XCircle, Trophy,
  UserPlus, BarChart3, PieChart, Sun, Moon
} from 'lucide-react';
import { format, subDays, isAfter, startOfDay } from 'date-fns';
import { cn } from '../lib/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';

// ============================================
// STAT CARD - SUPPORTS DARK/LIGHT MODE
// ============================================
function StatCard({ title, value, subtitle, icon: Icon, trend, trendLabel, colorClass, bgGradient, isDark, onClick }) {
  const isPositive = trend > 0;
  const TrendIcon = isPositive ? ArrowUpRight : ArrowDownRight;
  
  return (
    <Card 
      className={cn(
        "border-0 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden",
        isDark ? "bg-zinc-900/80" : "bg-white",
        onClick && "cursor-pointer"
      )}
      onClick={onClick}
    >
      <CardContent className="p-5 relative">
        <div className={cn("absolute top-0 right-0 w-24 h-24 opacity-[0.08] rounded-full -translate-y-8 translate-x-8", bgGradient || "bg-[#F4F3EE]0")} />
        
        <div className="flex items-start justify-between mb-3 relative">
          <div className={cn("p-2.5 rounded-xl", colorClass)}>
            <Icon className="w-5 h-5" />
          </div>
          {trend !== undefined && (
            <div className={cn(
              "flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full",
              isPositive 
                ? isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-50 text-emerald-600"
                : isDark ? "bg-red-500/20 text-red-400" : "bg-red-50 text-red-500"
            )}>
              <TrendIcon className="w-3 h-3" />
              {Math.abs(trend).toFixed(0)}%
            </div>
          )}
        </div>
        <p className={cn("text-3xl font-bold mb-1", isDark ? "text-white" : "text-[#1A1A1A]")}>{value}</p>
        <p className={cn("text-sm font-medium", isDark ? "text-zinc-400" : "text-[#9E8778]")}>{title}</p>
        {subtitle && <p className={cn("text-xs mt-1", isDark ? "text-zinc-500" : "text-[#9E8778]")}>{subtitle}</p>}
        {trendLabel && <p className={cn("text-xs mt-1", isDark ? "text-zinc-500" : "text-[#9E8778]")}>{trendLabel}</p>}
      </CardContent>
    </Card>
  );
}

// ============================================
// MINI BAR CHART
// ============================================
function MiniBarChart({ data, maxValue, color = "bg-[#F4F3EE]0", isDark }) {
  const max = maxValue || Math.max(...data.map(d => d.value), 1);
  
  return (
    <div className="flex items-end gap-1.5 h-20">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
          <div 
            className={cn("w-full rounded-md transition-all hover:opacity-80", color)}
            style={{ 
              height: `${(d.value / max) * 100}%`, 
              minHeight: d.value > 0 ? '6px' : '2px', 
              backgroundColor: d.value === 0 ? (isDark ? '#3f3f46' : '#e5e7eb') : undefined 
            }}
          />
          <span className={cn("text-[10px] font-medium", isDark ? "text-zinc-500" : "text-[#9E8778]")}>{d.label}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================
// USER ROW COMPONENT
// ============================================
function UserRow({ user, onTogglePremium, onToggleAdmin, onToggleBan, onDelete, isDark }) {
  return (
    <div className={cn(
      "p-4 flex items-center justify-between gap-4 border-b last:border-0 transition-colors",
      isDark 
        ? "hover:bg-zinc-800/50 border-zinc-800/50" 
        : "hover:bg-white border-violet-50"
    )}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-400 to-violet-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            {user.email?.charAt(0).toUpperCase()}
          </div>
          <span className={cn("font-medium truncate", isDark ? "text-white" : "text-[#1A1A1A]")}>{user.email}</span>
          {user.is_admin && (
            <Badge className={cn(
              "border-0 text-[10px] font-semibold",
              isDark ? "bg-[#F4F3EE]0/20 text-violet-400" : "bg-[#F4F3EE] text-[#1A1A1A]"
            )}>
              <Shield className="w-3 h-3 mr-1" />Admin
            </Badge>
          )}
          {user.is_premium && (
            <Badge className={cn(
              "border-0 text-[10px] font-semibold",
              isDark ? "bg-[#FDF0EC]0/20 text-coral" : "bg-[#FDF0EC] text-[#B04A32]"
            )}>
              <Crown className="w-3 h-3 mr-1" />Pro
            </Badge>
          )}
          {user.is_banned && (
            <Badge className={cn(
              "border-0 text-[10px] font-semibold",
              isDark ? "bg-red-500/20 text-red-400" : "bg-red-100 text-red-600"
            )}>
              <Ban className="w-3 h-3 mr-1" />Banned
            </Badge>
          )}
        </div>
        <div className={cn("flex items-center gap-4 text-xs ml-10", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
          {user.name && <span className={isDark ? "text-zinc-400" : "text-[#5F5F5F]"}>{user.name}</span>}
          <span className="flex items-center gap-1">
            <Target className={cn("w-3 h-3", isDark ? "text-zinc-500" : "text-[#9E8778]")} />
            {user.total_goals || 0} goals
          </span>
          <span className="flex items-center gap-1">
            <Activity className="w-3 h-3 text-emerald-500" />
            {user.active_goals || 0} active
          </span>
          <span className="flex items-center gap-1">
            <Calendar className={cn("w-3 h-3", isDark ? "text-zinc-500" : "text-[#9E8778]")} />
            {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : 'N/A'}
          </span>
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className={cn(
            "h-8 w-8 p-0",
            isDark ? "text-zinc-400 hover:text-white hover:bg-zinc-800" : "text-[#9E8778] hover:text-[#5F5F5F] hover:bg-white"
          )}>
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={cn(
          "shadow-lg",
          isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-[#EEEAE0]"
        )}>
          <DropdownMenuItem onClick={() => onTogglePremium(user.id, user.is_premium)} className={cn(
            isDark ? "text-zinc-300 focus:bg-zinc-800" : "text-[#5F5F5F] focus:bg-white"
          )}>
            <Crown className="w-4 h-4 mr-2 text-coral" />
            {user.is_premium ? 'Remove Pro' : 'Make Pro'}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onToggleAdmin(user.id, user.is_admin)} className={cn(
            isDark ? "text-zinc-300 focus:bg-zinc-800" : "text-[#5F5F5F] focus:bg-white"
          )}>
            <Shield className="w-4 h-4 mr-2 text-[#5F5F5F]" />
            {user.is_admin ? 'Remove Admin' : 'Make Admin'}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onToggleBan(user.id, user.is_banned)} className={cn(
            isDark ? "text-zinc-300 focus:bg-zinc-800" : "text-[#5F5F5F] focus:bg-white"
          )}>
            <Ban className="w-4 h-4 mr-2 text-coral" />
            {user.is_banned ? 'Unban User' : 'Ban User'}
          </DropdownMenuItem>
          <DropdownMenuSeparator className={isDark ? "bg-zinc-800" : "bg-white"} />
          <DropdownMenuItem onClick={() => onDelete(user.id)} className={cn(
            isDark ? "text-red-400 focus:bg-red-500/10" : "text-red-600 focus:bg-red-50"
          )}>
            <XCircle className="w-4 h-4 mr-2" />
            Delete User
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ============================================
// MAIN ADMIN PANEL
// ============================================
export default function AdminPanel() {
  const { user } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, usersRes] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get('/api/admin/users?limit=200')
      ]);
      setStats(statsRes.data);
      setAllUsers(usersRes.data.users || []);
      setUsersTotal(usersRes.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const filteredUsers = useMemo(() => {
    let filtered = allUsers;
    
    if (userFilter === 'premium') {
      filtered = filtered.filter(u => u.is_premium);
    } else if (userFilter === 'free') {
      filtered = filtered.filter(u => !u.is_premium);
    } else if (userFilter === 'new') {
      const weekAgo = subDays(new Date(), 7);
      filtered = filtered.filter(u => u.created_at && isAfter(new Date(u.created_at), weekAgo));
    }
    
    if (userSearch) {
      const search = userSearch.toLowerCase();
      filtered = filtered.filter(u => 
        u.email?.toLowerCase().includes(search) || 
        u.name?.toLowerCase().includes(search)
      );
    }
    
    return filtered;
  }, [allUsers, userFilter, userSearch]);

  const analytics = useMemo(() => {
    if (!allUsers.length) return null;
    
    const now = new Date();
    const weekAgo = subDays(now, 7);
    
    const newThisWeek = allUsers.filter(u => u.created_at && isAfter(new Date(u.created_at), weekAgo)).length;
    const newLastWeek = allUsers.filter(u => {
      if (!u.created_at) return false;
      const date = new Date(u.created_at);
      return isAfter(date, subDays(weekAgo, 7)) && !isAfter(date, weekAgo);
    }).length;
    
    const premiumUsers = allUsers.filter(u => u.is_premium).length;
    const conversionRate = allUsers.length > 0 ? (premiumUsers / allUsers.length * 100) : 0;
    const avgGoalsPerUser = allUsers.length > 0 ? (allUsers.reduce((sum, u) => sum + (u.total_goals || 0), 0) / allUsers.length) : 0;
    
    const dailySignups = [];
    for (let i = 6; i >= 0; i--) {
      const day = subDays(now, i);
      const dayStart = startOfDay(day);
      const dayEnd = startOfDay(subDays(now, i - 1));
      const count = allUsers.filter(u => {
        if (!u.created_at) return false;
        const date = new Date(u.created_at);
        return date >= dayStart && date < dayEnd;
      }).length;
      dailySignups.push({ label: format(day, 'EEE'), value: count });
    }
    
    return {
      newThisWeek, newLastWeek,
      weeklyGrowth: newLastWeek > 0 ? ((newThisWeek - newLastWeek) / newLastWeek * 100) : (newThisWeek > 0 ? 100 : 0),
      premiumUsers, conversionRate, avgGoalsPerUser, dailySignups
    };
  }, [allUsers]);

  const handleTogglePremium = async (userId, currentStatus) => {
    try {
      await api.patch(`/api/admin/users/${userId}`, { is_premium: !currentStatus });
      toast.success(`User ${!currentStatus ? 'upgraded to' : 'downgraded from'} premium`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    try {
      await api.patch(`/api/admin/users/${userId}`, { is_admin: !currentStatus });
      toast.success(`User ${!currentStatus ? 'promoted to' : 'demoted from'} admin`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleToggleBan = async (userId, currentStatus) => {
    try {
      await api.patch(`/api/admin/users/${userId}`, { is_banned: !currentStatus });
      toast.success(`User ${!currentStatus ? 'banned' : 'unbanned'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure? This will delete the user and all their data.')) return;
    try {
      await api.delete(`/api/admin/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  if (loading) {
    return (
      <div className={cn(
        "min-h-screen",
        isDark ? "bg-[#0a0a0a]" : "bg-[#FAF9F5]"
      )}>
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className={cn(
            "animate-spin w-10 h-10 border-4 rounded-full",
            isDark ? "border-zinc-800 border-t-violet-500" : "border-[#EEEAE0] border-t-violet-500"
          )}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      "min-h-screen transition-colors duration-300",
      isDark ? "bg-[#0a0a0a]" : "bg-[#FAF9F5]"
    )} data-testid="admin-panel">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#1A1A1A] rounded-2xl">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className={cn("text-2xl sm:text-3xl font-bold", isDark ? "text-white" : "text-[#1A1A1A]")}>
                  Admin Dashboard
                </h1>
                <p className={cn("text-sm mt-0.5", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
                  Platform analytics and user management
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <Button
              onClick={toggleTheme}
              variant="outline"
              size="icon"
              className={cn(
                "w-10 h-10 rounded-xl transition-all",
                isDark 
                  ? "bg-zinc-800 border-zinc-700 text-coral hover:bg-zinc-700 hover:text-amber-300" 
                  : "bg-white border-[#EEEAE0] text-[#5F5F5F] hover:bg-white hover:text-[#1A1A1A]"
              )}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            
            {/* Refresh Button */}
            <Button 
              onClick={refreshData} 
              className={cn(
                "gap-2 shadow-sm",
                isDark 
                  ? "bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700" 
                  : "bg-white border-[#EEEAE0] text-[#5F5F5F] hover:bg-white hover:border-[#E5E2DA]"
              )}
              variant="outline"
              disabled={refreshing}
            >
              <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard
            title="Total Users"
            value={stats?.users?.total || 0}
            subtitle={`${stats?.users?.premium || 0} Pro subscribers`}
            icon={Users}
            colorClass={isDark ? "bg-[#F4F3EE]0/20 text-violet-400" : "bg-[#F4F3EE] text-[#1A1A1A]"}
            bgGradient="bg-[#F4F3EE]0"
            trend={analytics?.weeklyGrowth}
            trendLabel="vs last week"
            isDark={isDark}
          />
          <StatCard
            title="New This Week"
            value={analytics?.newThisWeek || 0}
            subtitle={`${analytics?.newLastWeek || 0} last week`}
            icon={UserPlus}
            colorClass={isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-600"}
            bgGradient="bg-emerald-500"
            isDark={isDark}
          />
          <StatCard
            title="Pro Subscribers"
            value={stats?.users?.premium || 0}
            subtitle={`${analytics?.conversionRate?.toFixed(1) || 0}% conversion`}
            icon={Crown}
            colorClass={isDark ? "bg-[#FDF0EC]0/20 text-coral" : "bg-[#FDF0EC] text-coral"}
            bgGradient="bg-[#FDF0EC]0"
            isDark={isDark}
          />
          <StatCard
            title="Total Goals"
            value={stats?.goals?.total || 0}
            subtitle={`${stats?.goals?.active || 0} active`}
            icon={Target}
            colorClass={isDark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-600"}
            bgGradient="bg-blue-500"
            isDark={isDark}
          />
          <StatCard
            title="Completed"
            value={stats?.goals?.completed || 0}
            subtitle={`${stats?.goals?.failed || 0} failed`}
            icon={Trophy}
            colorClass={isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-600"}
            bgGradient="bg-emerald-500"
            isDark={isDark}
          />
          <StatCard
            title="Shared Goals"
            value={stats?.shared_goals?.total || 0}
            subtitle={`${stats?.shared_goals?.active || 0} active`}
            icon={Users}
            colorClass={isDark ? "bg-fuchsia-500/20 text-fuchsia-400" : "bg-fuchsia-100 text-fuchsia-600"}
            bgGradient="bg-fuchsia-500"
            isDark={isDark}
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Signups Chart */}
          <Card className={cn(
            "border-0 shadow-sm lg:col-span-2",
            isDark ? "bg-zinc-900/80" : "bg-white"
          )}>
            <CardHeader className="pb-2">
              <CardTitle className={cn("text-sm font-semibold flex items-center gap-2", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>
                <BarChart3 className="w-4 h-4 text-[#5F5F5F]" />
                New Signups (Last 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.dailySignups && (
                <MiniBarChart 
                  data={analytics.dailySignups} 
                  color="bg-[#1A1A1A]"
                  isDark={isDark}
                />
              )}
              <div className={cn(
                "flex items-center justify-between mt-6 pt-4 border-t",
                isDark ? "border-zinc-800" : "border-violet-50"
              )}>
                <div>
                  <p className={cn("text-3xl font-bold", isDark ? "text-white" : "text-[#1A1A1A]")}>{analytics?.newThisWeek || 0}</p>
                  <p className={cn("text-xs font-medium", isDark ? "text-zinc-500" : "text-[#9E8778]")}>Total this week</p>
                </div>
                <div className="text-right">
                  <p className={cn("text-xl font-bold", isDark ? "text-zinc-300" : "text-[#5F5F5F]")}>{(analytics?.newThisWeek / 7).toFixed(1)}</p>
                  <p className={cn("text-xs font-medium", isDark ? "text-zinc-500" : "text-[#9E8778]")}>Avg per day</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className={cn("border-0 shadow-sm", isDark ? "bg-zinc-900/80" : "bg-white")}>
            <CardHeader className="pb-2">
              <CardTitle className={cn("text-sm font-semibold flex items-center gap-2", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>
                <PieChart className="w-4 h-4 text-[#5F5F5F]" />
                User Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className={cn("font-medium", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>Pro vs Free</span>
                  <span className={cn("font-bold", isDark ? "text-coral" : "text-coral")}>{analytics?.conversionRate?.toFixed(1)}%</span>
                </div>
                <div className={cn("h-3 rounded-full overflow-hidden flex", isDark ? "bg-zinc-800" : "bg-white")}>
                  <div 
                    className="h-full bg-gradient-to-r from-amber-400 to-orange-400 transition-all rounded-full"
                    style={{ width: `${Math.max(analytics?.conversionRate || 0, 2)}%` }}
                  />
                </div>
                <div className={cn("flex justify-between text-xs mt-1.5 font-medium", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
                  <span>{stats?.users?.premium || 0} Pro</span>
                  <span>{stats?.users?.free || 0} Free</span>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className={cn("font-medium", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>Goal Completion</span>
                  <span className={cn("font-bold", isDark ? "text-emerald-400" : "text-emerald-600")}>
                    {stats?.goals?.total > 0 
                      ? ((stats.goals.completed / (stats.goals.completed + stats.goals.failed)) * 100).toFixed(1) 
                      : 0}%
                  </span>
                </div>
                <div className={cn("h-3 rounded-full overflow-hidden flex", isDark ? "bg-zinc-800" : "bg-white")}>
                  <div 
                    className="h-full bg-gradient-to-r from-emerald-400 to-teal-400 transition-all rounded-full"
                    style={{ 
                      width: `${stats?.goals?.total > 0 
                        ? Math.max((stats.goals.completed / (stats.goals.completed + stats.goals.failed)) * 100, 2)
                        : 2}%` 
                    }}
                  />
                </div>
                <div className={cn("flex justify-between text-xs mt-1.5 font-medium", isDark ? "text-zinc-500" : "text-[#9E8778]")}>
                  <span>{stats?.goals?.completed || 0} Won</span>
                  <span>{stats?.goals?.failed || 0} Lost</span>
                </div>
              </div>
              
              <div className={cn("pt-3 border-t", isDark ? "border-zinc-800" : "border-violet-50")}>
                <div className="flex items-center justify-between">
                  <span className={cn("text-sm font-medium", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>Avg Goals/User</span>
                  <span className={cn("text-2xl font-bold", isDark ? "text-white" : "text-[#1A1A1A]")}>{analytics?.avgGoalsPerUser?.toFixed(1) || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users Management */}
        <Card className={cn("border-0 shadow-sm", isDark ? "bg-zinc-900/80" : "bg-white")}>
          <CardHeader className={cn("border-b", isDark ? "border-zinc-800" : "border-violet-50")}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <CardTitle className={cn("text-lg font-semibold flex items-center gap-2", isDark ? "text-white" : "text-[#1A1A1A]")}>
                <Users className="w-5 h-5 text-[#5F5F5F]" />
                User Management
                <Badge className={cn("ml-2 border-0 font-medium", isDark ? "bg-zinc-800 text-zinc-400" : "bg-white text-[#5F5F5F]")}>
                  {filteredUsers.length} / {usersTotal}
                </Badge>
              </CardTitle>
              
              <div className="flex items-center gap-3">
                {/* Filter Tabs */}
                <div className={cn("flex rounded-xl p-1", isDark ? "bg-zinc-800" : "bg-white")}>
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'premium', label: 'Pro' },
                    { id: 'free', label: 'Free' },
                    { id: 'new', label: 'New' }
                  ].map(f => (
                    <button
                      key={f.id}
                      onClick={() => setUserFilter(f.id)}
                      className={cn(
                        "px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all",
                        userFilter === f.id 
                          ? isDark 
                            ? "bg-violet-600 text-white shadow-sm" 
                            : "bg-white text-[#1A1A1A] shadow-sm"
                          : isDark 
                            ? "text-zinc-400 hover:text-white" 
                            : "text-[#9E8778] hover:text-[#5F5F5F]"
                      )}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                
                {/* Search */}
                <div className="relative">
                  <Search className={cn("w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2", isDark ? "text-zinc-500" : "text-[#9E8778]")} />
                  <Input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search users..."
                    className={cn(
                      "pl-9 w-48 md:w-64",
                      isDark 
                        ? "bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-[#1A1A1A]" 
                        : "bg-white border-[#EEEAE0] text-[#1A1A1A] placeholder:text-[#9E8778] focus:border-violet-300"
                    )}
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {filteredUsers.length > 0 ? (
                filteredUsers.map(u => (
                  <UserRow
                    key={u.id}
                    user={u}
                    onTogglePremium={handleTogglePremium}
                    onToggleAdmin={handleToggleAdmin}
                    onToggleBan={handleToggleBan}
                    onDelete={handleDeleteUser}
                    isDark={isDark}
                  />
                ))
              ) : (
                <div className="p-12 text-center">
                  <div className={cn(
                    "w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4",
                    isDark ? "bg-zinc-800" : "bg-white"
                  )}>
                    <Users className={cn("w-8 h-8", isDark ? "text-zinc-600" : "text-[#9E8778]")} />
                  </div>
                  <p className={cn("font-medium", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>No users found</p>
                  <p className={cn("text-sm mt-1", isDark ? "text-zinc-600" : "text-[#9E8778]")}>Try adjusting your search or filter</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Revenue Info */}
        <div className={cn(
          "mt-8 p-6 border-0 shadow-sm rounded-2xl",
          isDark ? "bg-zinc-900/80" : "bg-white"
        )}>
          <h3 className={cn("text-lg font-semibold mb-5 flex items-center gap-2", isDark ? "text-white" : "text-[#1A1A1A]")}>
            <div className={cn("p-2 rounded-lg", isDark ? "bg-emerald-500/20" : "bg-emerald-100")}>
              <DollarSign className={cn("w-5 h-5", isDark ? "text-emerald-400" : "text-emerald-600")} />
            </div>
            Revenue Overview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className={cn(
              "p-5 rounded-xl border",
              isDark 
                ? "bg-[#F0EFE7] border-[#E5E2DA]" 
                : "bg-[#FDFCF9] border-emerald-100"
            )}>
              <p className={cn("text-3xl font-bold", isDark ? "text-emerald-400" : "text-emerald-600")}>
                ${((stats?.users?.premium || 0) * 4.99).toFixed(2)}
              </p>
              <p className={cn("text-sm font-medium mt-1", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>Monthly Recurring</p>
              <p className={cn("text-xs mt-0.5", isDark ? "text-zinc-600" : "text-[#9E8778]")}>{stats?.users?.premium || 0} × $4.99</p>
            </div>
            <div className={cn(
              "p-5 rounded-xl border",
              isDark 
                ? "bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-coral/20" 
                : "bg-[#FDF8F2] border-amber-100"
            )}>
              <p className={cn("text-3xl font-bold", isDark ? "text-coral" : "text-coral")}>
                ${((stats?.users?.premium || 0) * 4.99 * 12).toFixed(0)}
              </p>
              <p className={cn("text-sm font-medium mt-1", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>Projected Annual</p>
              <p className={cn("text-xs mt-0.5", isDark ? "text-zinc-600" : "text-[#9E8778]")}>Based on current MRR</p>
            </div>
            <div className={cn(
              "p-5 rounded-xl border",
              isDark 
                ? "bg-[#F4F3EE] border-[#E5E2DA]" 
                : "bg-[#F4F3EE] border-[#EEEAE0]"
            )}>
              <p className={cn("text-3xl font-bold", isDark ? "text-violet-400" : "text-[#1A1A1A]")}>{stats?.users?.total || 0}</p>
              <p className={cn("text-sm font-medium mt-1", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>Total Users</p>
              <p className={cn("text-xs mt-0.5", isDark ? "text-zinc-600" : "text-[#9E8778]")}>All-time signups</p>
            </div>
            <div className={cn(
              "p-5 rounded-xl border",
              isDark 
                ? "bg-gradient-to-br from-zinc-800 to-zinc-900 border-zinc-700" 
                : "bg-gradient-to-br from-gray-50 to-slate-50 border-[#EEEAE0]"
            )}>
              <p className={cn("text-3xl font-bold", isDark ? "text-white" : "text-gray-800")}>
                {stats?.goals?.active || 0}
              </p>
              <p className={cn("text-sm font-medium mt-1", isDark ? "text-zinc-400" : "text-[#5F5F5F]")}>Active Goals</p>
              <p className={cn("text-xs mt-0.5", isDark ? "text-zinc-600" : "text-[#9E8778]")}>Currently tracked</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
